import dayjs from "dayjs";
import { SegmentedMessage } from "sms-segments-calculator";
import { timeBoundMessageTypes } from "../../App/Constants/MessageConstants";
import { IMessageHour, IMessageMinute, MessageType } from "../../App/Typings/MessageText";
import { toNearestFiveMinutes } from "../../MessagingCenter/Utils";

// Message lengths and error messages
const maxSubjectLength = 100;
const maxEmailBodyLength = 1500;
const maxTextOverallLength = 600;
const minTextSegmentThreshold = 35; // Minimum length to check if an SMS message will be segmented
const minMessageLengthError = "Message cannot be empty";
const maxSubjectLengthError = `Subject cannot be longer than ${maxSubjectLength} characters`;
const minSubjectLengthError = "Subject cannot be empty";
const messageContainsLinkNote =
  "The link in this message will automatically be shortened to save on character count.";

const generateScheduleDateErrorMessage = (payload: { time: string | null }) => {
  const utcTime = toNearestFiveMinutes(dayjs(payload.time));
  if (!utcTime.isValid()) {
    return "Invalid Date";
  } else if (!utcTime.isAfter(dayjs().utc())) {
    return "Scheduled date must be in the future";
  }
  return undefined;
};

// Message length and error message helper functions
const getMaxMessageLength = (isEmail: boolean) => {
  return isEmail ? maxEmailBodyLength : maxTextOverallLength;
};

const getMaxMessageLengthError = (isEmail: boolean) => {
  return `Message cannot be longer than ${getMaxMessageLength(isEmail)} characters`;
};

const textMessageContainsLink = (body: string, isEmail: boolean) => {
  return (
    !isEmail &&
    body.match(
      /(?:https?:\/\/)?(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)/,
    )
  );
};

const generateBodyErrorMessage = (incomingMessageBody: string, isEmail: boolean, touched: boolean) => {
  const messageLength = incomingMessageBody.trim().length;
  const maxMessageLength = getMaxMessageLength(isEmail);
  const maxMessageLengthError = getMaxMessageLengthError(isEmail);

  let bodyErrorMessage = undefined;

  if (messageLength > maxMessageLength) {
    // CRM-485 Remove max length validation for Email messages until Large Email Templates are implemented
    // bodyErrorMessage = maxMessageLengthError;
    if (!isEmail) {
      bodyErrorMessage = maxMessageLengthError;
    }
  } else if (messageLength === 0 && touched) {
    bodyErrorMessage = minMessageLengthError;
  }

  return bodyErrorMessage;
};

const generateTextMessageSplit = (incomingMessageBody: string, isEmail: boolean) => {
  const textLength = incomingMessageBody.trim().length;
  const messageTextSplit = !isEmail && textLength >= minTextSegmentThreshold;

  let textSplitCount = undefined;
  let textSplitWarning = undefined;

  if (messageTextSplit) {
    const estimatedSegments = new SegmentedMessage(incomingMessageBody.trim()).segmentsCount;
    if (estimatedSegments > 1) {
      const splitMessageWarning = `Your message will be split into ${estimatedSegments} messages, each will be treated as a separate text message in your quota.`;
      textSplitCount = estimatedSegments;
      textSplitWarning = splitMessageWarning;
    }
  }

  return { textSplitCount, textSplitWarning };
};

const generateSubjectErrorMessage = (incomingSubject: string) => {
  const subjectLength = incomingSubject.trim().length;
  let subjectErrorMessage = undefined;

  if (subjectLength > maxSubjectLength) {
    subjectErrorMessage = maxSubjectLengthError;
  } else if (subjectLength === 0) {
    subjectErrorMessage = minSubjectLengthError;
  }

  return subjectErrorMessage;
};

const generateTemplateNameErrorMessage = (incomingTemplateName: string) => {
  const templateNameTooShort = incomingTemplateName.trim().length === 0;
  return templateNameTooShort ? "Template Name cannot be empty" : undefined;
};

const generateTemplateVisibilityErrorMessage = (incomingTemplateVisibility: string) => {
  const templateVisibilityTooShort = incomingTemplateVisibility.trim().length === 0;
  return templateVisibilityTooShort ? "Template Visibility cannot be empty" : undefined;
};

// Message hours and minutes helper functions
const getScheduleTimeDisplay = (
  hour: number,
  minutes: number,
  messageHours: Array<IMessageHour>,
  messageMinutes: Array<IMessageMinute>,
  messageType: MessageType,
) => {
  if (
    !isValidMessageHour(hour, messageHours) ||
    !isValidMessageMinute(minutes, hour, messageHours, messageMinutes, messageType)
  ) {
    return "Invalid Time";
  }
  const hourDisplay = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
  const minutesDisplay = minutes < 10 ? `0${minutes}` : minutes;
  const suffix = hour < 12 ? "AM" : "PM";
  return `${hourDisplay}:${minutesDisplay} ${suffix}`;
};

const isValidMessageHour = (hour: number, messageHours: Array<IMessageHour>) => {
  return messageHours.filter((h) => h.value === hour).length > 0;
};

const isValidMessageMinute = (
  minute: number,
  hour: number,
  messageHours: Array<IMessageHour>,
  messageMinutes: Array<IMessageMinute>,
  messageType: MessageType,
) => {
  if (
    hour ===
      Math.max.apply(
        null,
        messageHours.map((c) => c.value),
      ) &&
    timeBoundMessageTypes.includes(messageType)
  ) {
    return minute === 0;
  }
  return messageMinutes.filter((m) => m.value === minute).length > 0;
};

export {
  generateBodyErrorMessage,
  generateScheduleDateErrorMessage,
  generateSubjectErrorMessage,
  generateTemplateNameErrorMessage,
  generateTemplateVisibilityErrorMessage,
  generateTextMessageSplit,
  getMaxMessageLength,
  getMaxMessageLengthError,
  getScheduleTimeDisplay,
  isValidMessageHour,
  isValidMessageMinute,
  maxEmailBodyLength,
  maxSubjectLength,
  maxSubjectLengthError,
  maxTextOverallLength,
  minTextSegmentThreshold,
  messageContainsLinkNote,
  minMessageLengthError,
  minSubjectLengthError,
  textMessageContainsLink,
};
